import { useEffect } from 'react'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { tinykeys } from 'tinykeys'
import { isTypingTarget } from '@/lib/hotkeys'
import { GLOBAL_TOPIC, useLiveTopic } from '@/lib/live'
import { useHelp } from '@/stores/help'
import { useLayout } from '@/stores/layout'
import { TooltipProvider } from '@/components/ui'
import { HelpSheet } from './HelpSheet'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { isSessionPath } from '@/features/sessions/data'
import { ResizeHandle } from '@/components/ResizeHandle'
import { NewVersion } from './NewVersion'
import { Banners, Toasts } from './Banners'

/**
 * Every signed-in screen renders inside this: sidebar, top bar, banners. Project and workspace sockets belong to the screens that show them;
 * the global one is held here, for every signed-in screen.
 */
export function Shell() {
  const nav = useNavigate()
  const setHelp = useHelp((s) => s.setHelp)
  useLiveTopic(GLOBAL_TOPIC)
  // An open session gets the width: the sidebar folds away until the top bar's toggle brings it back.
  const inSession = useRouterState({ select: (s) => isSessionPath(s.location.pathname) })
  const nav2 = useLayout((s) => s.navInSession)
  const showNav = !inSession || nav2

  useEffect(() => {
    const guard = (fn: () => void) => (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return
      e.preventDefault()
      fn()
    }
    return tinykeys(window, {
      'Shift+?': guard(() => setHelp(true)),
      'g h': guard(() => void nav({ to: '/' })),
      'g s': guard(() => void nav({ to: '/servers' })),
      'g u': guard(() => void nav({ to: '/settings/usage' })),
      'g p': guard(() => void nav({ to: '/settings/people' })),
    })
  }, [nav, setHelp])

  return (
    <TooltipProvider>
      <div className="flex h-full">
        {showNav && <><Sidebar /><SidebarHandle /></>}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar />
          <Banners />
          <main className="min-h-0 flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
      <NewVersion />
      <HelpSheet />
      <Toasts />
    </TooltipProvider>
  )
}

/** Drag to resize the sidebar (180–480px, remembered). */
function SidebarHandle() {
  const setSidebarWidth = useLayout((s) => s.setSidebarWidth)
  return <ResizeHandle label="Resize sidebar" title="Drag to resize" onResize={setSidebarWidth} />
}
